import { api, ApiError } from './api';
import {
  BuyerCredit,
  BuyerCreditStatus,
  CreateDealerRequest,
  CreateDealerUserRequest,
  CreditPack,
  CreditRequest,
  CreditRequestKind,
  CreditRequestStatus,
  Dealer,
  DealerAction,
  DealerDocument,
  DealerDocumentType,
  DealerLot,
  DealerPage,
  DealerRefund,
  DealerStatus,
  DealerUser,
  MarkPaidRequest,
  MarkPaidResult,
  PackRequest,
  RevokeAfterPaidRequest,
  Showroom,
  ShowroomRequest,
  UpdateDealerRequest,
  UpdateDealerUserRequest,
  UploadUrl,
} from '../types/dealers';

const BASE = '/api/admin/dealers';
const enc = encodeURIComponent;

function query(params: Record<string, string | number | null | undefined>): string {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
}

/**
 * Spec 085 — Admin → Dealers, served by **orders** (`AdminDealerController`, gateway route
 * `orders-admin-dealers`, screen `dealers`). Errors use `{code, message, details}` (details also
 * flattened to the top level). With the switch off (D14) every write answers 404 `FEATURE_OFF`; reads,
 * the request mark-paid and the dealer-refund mark-paid stay open. Revoke-after-paid is super admin only.
 */
export const dealersApi = {
  list: (params: { status?: DealerStatus; q?: string; cursor?: string; limit?: number }, token: string) =>
    api.get<DealerPage<Dealer>>(`${BASE}${query({ ...params })}`, token),

  /** 201 DRAFT (+ breakEven) · 403 DISCOUNT_ABOVE_BREAK_EVEN_NEEDS_FINANCE · 422 VALIDATION */
  create: (input: CreateDealerRequest, token: string) =>
    api.post<Dealer>(BASE, input, token),

  get: (id: string, token: string) =>
    api.get<Dealer>(`${BASE}/${enc(id)}`, token),

  /** A `discountPct` edit returns `breakEven`. 403 DISCOUNT_ABOVE_BREAK_EVEN_NEEDS_FINANCE. */
  update: (id: string, input: UpdateDealerRequest, token: string) =>
    api.patch<Dealer>(`${BASE}/${enc(id)}`, input, token),

  /** 422 AGREEMENT_MISSING · 409 INVALID_STATE */
  activate: (id: string, token: string) =>
    api.post<Dealer>(`${BASE}/${enc(id)}/activate`, {}, token),

  suspend: (id: string, token: string) =>
    api.post<Dealer>(`${BASE}/${enc(id)}/suspend`, {}, token),

  /** 422 AGREEMENT_MISSING · 409 INVALID_STATE */
  reinstate: (id: string, token: string) =>
    api.post<Dealer>(`${BASE}/${enc(id)}/reinstate`, {}, token),

  agreementUploadUrl: (id: string, token: string) =>
    api.post<UploadUrl>(`${BASE}/${enc(id)}/agreement/upload-url`, {}, token),

  /** Browser → S3 PUT of the signed PDF to the presigned URL (no auth header). */
  putAgreementToStorage: async (uploadUrl: string, file: Blob): Promise<void> => {
    const res = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': 'application/pdf' }, body: file });
    if (!res.ok) throw new ApiError(`Upload failed (HTTP ${res.status})`, res.status);
  },

  /** 201 — `signedAt` is a `YYYY-MM-DD` date. */
  confirmAgreement: (id: string, body: { objectKey: string; signedAt: string }, token: string) =>
    api.post<Dealer>(`${BASE}/${enc(id)}/agreement`, body, token),

  /** Presigned PUT, then confirm: the whole agreement upload. */
  uploadAgreement: async (id: string, file: Blob, signedAt: string, token: string): Promise<Dealer> => {
    const { uploadUrl, storageKey } = await dealersApi.agreementUploadUrl(id, token);
    await dealersApi.putAgreementToStorage(uploadUrl, file);
    return dealersApi.confirmAgreement(id, { objectKey: storageKey, signedAt }, token);
  },

  deleteAgreement: (id: string, token: string) =>
    api.delete<void>(`${BASE}/${enc(id)}/agreement`, token),

  audit: (id: string, token: string, limit = 100) =>
    api.get<DealerAction[]>(`${BASE}/${enc(id)}/audit${query({ limit })}`, token),

  // ── users ──

  users: (id: string, token: string) =>
    api.get<DealerUser[]>(`${BASE}/${enc(id)}/users`, token),

  /** 201 · 409 IDENTITY_CONFLICT · 409 USERNAME_TAKEN · 422 USERNAME_INVALID · 503 IDENTITY_UNAVAILABLE */
  createUser: (id: string, input: CreateDealerUserRequest, token: string) =>
    api.post<DealerUser>(`${BASE}/${enc(id)}/users`, input, token),

  updateUser: (id: string, userId: string, input: UpdateDealerUserRequest, token: string) =>
    api.patch<DealerUser>(`${BASE}/${enc(id)}/users/${enc(userId)}`, input, token),

  // ── showrooms ──

  showrooms: (id: string, token: string) =>
    api.get<Showroom[]>(`${BASE}/${enc(id)}/showrooms`, token),

  /** 422 SHOWROOM_LIMIT (20 active) */
  createShowroom: (id: string, input: ShowroomRequest, token: string) =>
    api.post<Showroom>(`${BASE}/${enc(id)}/showrooms`, input, token),

  updateShowroom: (id: string, sid: string, input: ShowroomRequest, token: string) =>
    api.patch<Showroom>(`${BASE}/${enc(id)}/showrooms/${enc(sid)}`, input, token),

  /** Deactivates (soft delete). */
  deleteShowroom: (id: string, sid: string, token: string) =>
    api.delete<void>(`${BASE}/${enc(id)}/showrooms/${enc(sid)}`, token),

  // ── requests, credits, lots, refunds, documents ──

  requests: (id: string, params: { status?: CreditRequestStatus; kind?: CreditRequestKind; cursor?: string; limit?: number }, token: string) =>
    api.get<DealerPage<CreditRequest>>(`${BASE}/${enc(id)}/requests${query({ ...params })}`, token),

  /** 422 AMOUNT_MISMATCH (details.expectedAed) · 409 ALREADY_PAID | INVALID_STATE. Open with the switch off. */
  markPaid: (id: string, rid: string, input: MarkPaidRequest, token: string) =>
    api.post<MarkPaidResult>(`${BASE}/${enc(id)}/requests/${enc(rid)}/mark-paid`, input, token),

  /** Pending only; 409 CREDIT_LIVE_IMMUTABLE once paid. */
  revokeRequest: (id: string, rid: string, reason: string, token: string) =>
    api.post<CreditRequest>(`${BASE}/${enc(id)}/requests/${enc(rid)}/revoke`, { reason }, token),

  credits: (id: string, params: { status?: BuyerCreditStatus; cursor?: string; limit?: number }, token: string) =>
    api.get<DealerPage<BuyerCredit>>(`${BASE}/${enc(id)}/credits${query({ ...params })}`, token),

  /** Super admin. 409 CREDIT_RESERVED · 422 AMOUNT_EXCEEDS_REMAINING | REFUND_EXCEEDS_REVOKED. Issues a DCN. */
  revokeCreditAfterPaid: (id: string, cid: string, input: RevokeAfterPaidRequest, token: string) =>
    api.post<DealerRefund>(`${BASE}/${enc(id)}/credits/${enc(cid)}/revoke-after-paid`, input, token),

  lots: (id: string, token: string) =>
    api.get<DealerLot[]>(`${BASE}/${enc(id)}/lots`, token),

  /** Super admin. Same errors as the credit revoke. */
  revokeLotAfterPaid: (id: string, lid: string, input: RevokeAfterPaidRequest, token: string) =>
    api.post<DealerRefund>(`${BASE}/${enc(id)}/lots/${enc(lid)}/revoke-after-paid`, input, token),

  dealerRefunds: (id: string, token: string) =>
    api.get<DealerRefund[]>(`${BASE}/${enc(id)}/dealer-refunds`, token),

  /** 409 ALREADY_PAID. Open with the switch off. */
  markDealerRefundPaid: (id: string, drid: string, input: { bankReference: string; valueDate: string }, token: string) =>
    api.post<DealerRefund>(`${BASE}/${enc(id)}/dealer-refunds/${enc(drid)}/mark-paid`, input, token),

  documents: (id: string, token: string, type?: DealerDocumentType) =>
    api.get<DealerDocument[]>(`${BASE}/${enc(id)}/documents${query({ type })}`, token),

  /** Emails a sent or failed document again (a queued send is answered as is). 404 NOT_FOUND · 409 INVALID_STATE. Open with the switch off. */
  resendDocument: (id: string, documentId: string, token: string) =>
    api.post<DealerDocument>(`${BASE}/${enc(id)}/documents/${enc(documentId)}/resend`, {}, token),

  // ── packs ──

  packs: (token: string) =>
    api.get<CreditPack[]>(`${BASE}/packs`, token),

  pack: (pid: string, token: string) =>
    api.get<CreditPack>(`${BASE}/packs/${enc(pid)}`, token),

  createPack: (input: PackRequest, token: string) =>
    api.post<CreditPack>(`${BASE}/packs`, input, token),

  updatePack: (pid: string, input: PackRequest, token: string) =>
    api.patch<CreditPack>(`${BASE}/packs/${enc(pid)}`, input, token),

  /** `{lowestCommissionPct}`, null when unknown (cached 10 min server-side). */
  breakEven: (token: string) =>
    api.get<{ lowestCommissionPct: string | null }>(`${BASE}/break-even`, token),
};

export const FEATURE_OFF_MESSAGE = 'Dealer credit is switched off.';

/** D14 / BR-33 — the switch is off: the caller shows the banner instead of an error. */
export function isFeatureOff(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404 && err.code === 'FEATURE_OFF';
}

/** The `{code}` of a dealer credit error, if it is one. */
export function dealerErrorCode(err: unknown): string | undefined {
  return err instanceof ApiError ? err.code : undefined;
}

/** A detail the backend attaches to the error body (`details` and also flattened). */
export function dealerErrorDetail(err: unknown, key: string): string | undefined {
  if (!(err instanceof ApiError) || !err.details) return undefined;
  const v = err.details[key];
  return v === undefined || v === null ? undefined : String(v);
}

/** The requirements Permissions row: a discount at or above break-even needs finance-config too. */
export function needsFinanceMessage(err: unknown): string {
  const lowest = dealerErrorDetail(err, 'lowestCommissionPct');
  return lowest
    ? `This discount is at or above break-even (lowest live commission ${Number(lowest)}%). An admin with finance configuration access must save it.`
    : 'The lowest live commission is unknown right now, so any discount change needs an admin with finance configuration access.';
}

/** Admin copy for the dealer credit codes this portal can hit; anything else falls back to the server text. */
const MESSAGES: Record<string, string> = {
  FEATURE_OFF: FEATURE_OFF_MESSAGE,
  DEALER_NOT_FOUND: 'Dealer not found.',
  NOT_FOUND: 'Not found. Reload the page.',
  AGREEMENT_MISSING: 'Upload the signed agreement before activating.',
  IDENTITY_CONFLICT: 'This phone or email already belongs to another dealer user.',
  USERNAME_TAKEN: 'This username is already taken. Choose another.',
  SHOWROOM_LIMIT: 'A dealer can have at most 20 active showrooms.',
  ALREADY_PAID: 'This item is already marked paid.',
  CREDIT_LIVE_IMMUTABLE: 'Paid credit cannot be revoked here. Use “Revoke after paid” on the Credits tab.',
  CREDIT_RESERVED: 'Part of this credit is held by a booking. Try again later.',
  AMOUNT_EXCEEDS_REMAINING: 'The amount is more than the unreserved remaining.',
  REFUND_EXCEEDS_REVOKED: 'The cash refund cannot exceed the revoked amount.',
  INVALID_AMOUNT: 'Enter a valid AED amount.',
  INVALID_PHONE: 'Enter a valid mobile number.',
  IDENTITY_UNAVAILABLE: 'Account provisioning is unavailable right now. Try again.',
};

export function dealerErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (err instanceof ApiError) {
    if (err.code === 'DISCOUNT_ABOVE_BREAK_EVEN_NEEDS_FINANCE') return needsFinanceMessage(err);
    if (err.code === 'AMOUNT_MISMATCH') {
      const expected = dealerErrorDetail(err, 'expectedAed');
      return expected ? `The received amount must equal the amount due (AED ${expected}).` : 'The received amount must equal the amount due.';
    }
    if (err.code && MESSAGES[err.code]) return MESSAGES[err.code];
    // INVALID_STATE carries the reason (e.g. "Suspend the dealer before removing its agreement.").
    if (err.code === 'INVALID_STATE') return err.serverMessage ?? 'This action is not allowed in the current state. Reload the page.';
    return err.serverMessage ?? err.message;
  }
  return err instanceof Error ? err.message : fallback;
}

/** The field a 422 names (`details.field`, or the bean-validation `errors` map), else null. */
export function dealerErrorFields(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError)) return {};
  if (err.errors) return { ...err.errors };
  const field = dealerErrorDetail(err, 'field');
  return field ? { [field]: err.serverMessage ?? err.message } : {};
}
